import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  BookOpen, Brain, TrendingUp, Award, Check, Star,
  BarChart3, Clock, Zap, ArrowRight, Users, Target
} from 'lucide-react';

/**
 * Public Landing Page - First page visitors see
 * Marketing page that converts visitors to users
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, isLoading, error } = useAuth0();
  const [authError, setAuthError] = React.useState(null);

  // Check for Auth0 error query parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (errorParam || error) {
      let friendlyMessage = '';
      const errorMsg = errorDescription || error?.message || errorParam || '';

      // Handle common Auth0 errors with user-friendly messages
      if (errorMsg.toLowerCase().includes('already exists') ||
          errorMsg.toLowerCase().includes('user already exists')) {
        friendlyMessage = 'This email is already registered. Please sign in instead of signing up.';
      } else if (errorMsg.toLowerCase().includes('invalid') && errorMsg.toLowerCase().includes('email')) {
        friendlyMessage = 'Please use a valid email address. Some email domains (like @email.com or @test.com) are not accepted.';
      } else if (errorMsg.toLowerCase().includes('disposable') ||
                 errorMsg.toLowerCase().includes('temporary')) {
        friendlyMessage = 'Disposable or temporary email addresses are not allowed. Please use a permanent email address.';
      } else if (errorMsg.toLowerCase().includes('blocked')) {
        friendlyMessage = 'This email domain is blocked. Please use a different email provider (Gmail, Yahoo, Outlook, etc.).';
      } else if (errorParam === 'access_denied') {
        friendlyMessage = 'Access was denied. If you already have an account, please sign in instead.';
      } else if (errorMsg) {
        friendlyMessage = errorMsg;
      }

      if (friendlyMessage) {
        setAuthError(friendlyMessage);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [error]);

  // Automatically redirect authenticated users to /app
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/app');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/app');
    } else {
      navigate('/pricing');
    }
  };

  const handleStartFreeTrial = () => {
    if (isAuthenticated) {
      navigate('/app');
    } else {
      loginWithRedirect({
        appState: { returnTo: '/app' }
      });
    }
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/soldier-flag-bg.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Semi-transparent overlay for entire page */}
      <div className="absolute inset-0 bg-white/20 pointer-events-none" style={{ zIndex: 0 }}></div>

      {/* Content wrapper */}
      <div className="relative" style={{ zIndex: 1 }}>
      {/* Navigation */}
      <nav className="bg-gradient-to-r from-blue-900 via-white to-red-700 border-b-4 border-blue-800 sticky top-0 z-50 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-white/95 backdrop-blur">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-700 via-white to-red-700 rounded-full flex items-center justify-center shadow-lg">
                <Star className="w-5 h-5 text-blue-800 fill-blue-800" />
              </div>
              <span className="text-2xl font-black bg-gradient-to-r from-blue-900 via-red-700 to-blue-900 bg-clip-text text-transparent tracking-tight">StartPatriot</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-800 hover:text-blue-800 transition font-semibold">Features</a>
              <a href="#how-it-works" className="text-gray-800 hover:text-blue-800 transition font-semibold">How It Works</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="text-gray-800 hover:text-blue-800 transition font-semibold">Pricing</a>
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/app')}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-700 via-blue-800 to-red-700 text-white rounded-lg hover:from-blue-800 hover:to-red-800 transition font-bold shadow-lg"
                >
                  Login / Sign Up
                </button>
              ) : (
                <>
                  <button
                    onClick={() => loginWithRedirect()}
                    className="text-gray-800 hover:text-blue-800 transition font-semibold"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleStartFreeTrial}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-700 via-blue-800 to-red-700 text-white rounded-lg hover:from-blue-800 hover:to-red-800 transition font-bold shadow-lg flex items-center gap-2"
                  >
                    <Star className="w-4 h-4 fill-white" />
                    Start Free Trial
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Auth Error Banner */}
      {authError && (
        <div className="bg-red-100 border-l-4 border-red-500 p-4 mx-auto max-w-4xl mt-4 rounded-r-lg shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-red-800 font-semibold">{authError}</p>
              {authError.includes('sign in') && (
                <button
                  onClick={() => loginWithRedirect()}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-bold text-sm"
                >
                  Sign In Now
                </button>
              )}
            </div>
            <button
              onClick={() => setAuthError(null)}
              className="flex-shrink-0 ml-4 text-red-500 hover:text-red-700"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-100/20 via-white/20 to-red-100/20 py-20 sm:py-32">
        {/* Patriotic Background Stripes */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <div className="absolute top-0 left-0 right-0 h-16 bg-red-700"></div>
          <div className="absolute top-20 left-0 right-0 h-16 bg-blue-800"></div>
          <div className="absolute top-40 left-0 right-0 h-16 bg-red-700"></div>
          <div className="absolute bottom-40 left-0 right-0 h-16 bg-blue-800"></div>
          <div className="absolute bottom-20 left-0 right-0 h-16 bg-red-700"></div>
        </div>

        {/* Star field background */}
        <div className="absolute inset-0 overflow-hidden opacity-10">
          {[...Array(20)].map((_, i) => (
            <Star key={i} className={`absolute w-${4 + (i % 4)} h-${4 + (i % 4)} text-blue-800 fill-blue-800`} style={{ top: `${(i * 47) % 100}%`, left: `${(i * 73) % 100}%` }} />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-700 to-red-700 text-white px-6 py-3 rounded-full text-sm font-bold mb-8 shadow-xl border-4 border-white">
              <Award className="w-5 h-5" />
              TRUSTED BY 12,500+ MILITARY RECRUITS
              <Award className="w-5 h-5" />
            </div>

            <div className="p-8 max-w-4xl mx-auto mb-10">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight mb-6 leading-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
                ACE THE <span className="text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">ASVAB</span>
                <br />AND SERVE YOUR COUNTRY
              </h1>

              <p className="text-xl sm:text-2xl text-white max-w-3xl mx-auto font-bold drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
                Join thousands of patriots who scored higher using our comprehensive ASVAB prep,
                practice tests, and adaptive learning system.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={handleStartFreeTrial}
                className="group px-10 py-5 bg-gradient-to-r from-blue-700 via-blue-800 to-red-700 text-white text-xl font-black rounded-xl hover:from-blue-800 hover:to-red-800 transition shadow-2xl hover:shadow-3xl flex items-center gap-3 border-4 border-white uppercase"
              >
                <Star className="w-6 h-6 fill-white" />
                START 7-DAY FREE TRIAL
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition" />
              </button>

              <button
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                className="px-10 py-5 bg-white text-blue-900 text-xl font-bold rounded-xl border-4 border-blue-800 hover:bg-blue-50 transition shadow-xl hover:border-red-700"
              >
                See How It Works
              </button>
            </div>

            <div className="mt-6 inline-block px-8 py-3">
              <p className="text-base text-white font-bold uppercase tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                No credit card required • Cancel anytime
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center bg-gradient-to-br from-blue-700 to-blue-900 text-white p-6 rounded-xl shadow-xl">
              <div className="text-5xl font-black mb-2">5,000+</div>
              <div className="font-bold uppercase text-sm tracking-wide">Practice Questions</div>
            </div>
            <div className="text-center bg-gradient-to-br from-red-700 to-red-900 text-white p-6 rounded-xl shadow-xl">
              <div className="text-5xl font-black mb-2">92%</div>
              <div className="font-bold uppercase text-sm tracking-wide">Score Improvement</div>
            </div>
            <div className="text-center bg-gradient-to-br from-blue-800 to-blue-950 text-white p-6 rounded-xl shadow-xl">
              <div className="text-5xl font-black mb-2">12,500</div>
              <div className="font-bold uppercase text-sm tracking-wide">Recruits Trained</div>
            </div>
            <div className="text-center bg-gradient-to-br from-red-800 to-red-950 text-white p-6 rounded-xl shadow-xl">
              <div className="text-5xl font-black mb-2 flex items-center justify-center gap-2">
                <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
                4.9
              </div>
              <div className="font-bold uppercase text-sm tracking-wide">Star Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-white/95 via-blue-50/95 to-red-50/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="p-8 max-w-5xl mx-auto">
              <h2 className="text-5xl font-black text-white mb-4 uppercase tracking-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
                Everything You Need to Ace the ASVAB
              </h2>
              <p className="text-xl text-white font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                COMPREHENSIVE PREP COVERING ALL 10 ASVAB SUBTESTS
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-8 bg-gradient-to-br from-blue-700 to-blue-900 text-white rounded-2xl hover:shadow-2xl hover:scale-105 transition border-4 border-white">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-xl">
                <BookOpen className="w-8 h-8 text-blue-800" />
              </div>
              <h3 className="text-2xl font-black mb-3 uppercase">All 10 ASVAB Subtests</h3>
              <p className="text-blue-100 font-medium">
                Complete coverage of Arithmetic Reasoning, Word Knowledge, Math Knowledge, and all other ASVAB sections.
              </p>
            </div>

            <div className="p-8 bg-gradient-to-br from-red-700 to-red-900 text-white rounded-2xl hover:shadow-2xl hover:scale-105 transition border-4 border-white">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-xl">
                <Target className="w-8 h-8 text-red-800" />
              </div>
              <h3 className="text-2xl font-black mb-3 uppercase">Full-Length Practice Tests</h3>
              <p className="text-red-100 font-medium">
                Realistic ASVAB practice exams that simulate actual test conditions with instant scoring.
              </p>
            </div>

            <div className="p-8 bg-gradient-to-br from-blue-800 to-blue-950 text-white rounded-2xl hover:shadow-2xl hover:scale-105 transition border-4 border-white">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-xl">
                <BarChart3 className="w-8 h-8 text-blue-900" />
              </div>
              <h3 className="text-2xl font-black mb-3 uppercase">AFQT Score Tracking</h3>
              <p className="text-blue-100 font-medium">
                Monitor your Armed Forces Qualification Test score and track progress across all subtests.
              </p>
            </div>

            <div className="p-8 bg-gradient-to-br from-red-800 to-red-950 text-white rounded-2xl hover:shadow-2xl hover:scale-105 transition border-4 border-white">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-xl">
                <Clock className="w-8 h-8 text-red-900" />
              </div>
              <h3 className="text-2xl font-black mb-3 uppercase">Flexible Study Plans</h3>
              <p className="text-red-100 font-medium">
                Customized prep schedules designed around your MEPS test date and availability.
              </p>
            </div>

            <div className="p-8 bg-gradient-to-br from-blue-700 via-blue-800 to-red-800 text-white rounded-2xl hover:shadow-2xl hover:scale-105 transition border-4 border-white">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-xl">
                <Award className="w-8 h-8 text-blue-900" />
              </div>
              <h3 className="text-2xl font-black mb-3 uppercase">MOS Qualification Insights</h3>
              <p className="text-blue-100 font-medium">
                Discover which military jobs you qualify for based on your practice scores.
              </p>
            </div>

            <div className="p-8 bg-gradient-to-br from-red-700 via-red-800 to-blue-800 text-white rounded-2xl hover:shadow-2xl hover:scale-105 transition border-4 border-white">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-xl">
                <Zap className="w-8 h-8 text-red-900" />
              </div>
              <h3 className="text-2xl font-black mb-3 uppercase">Study Anywhere</h3>
              <p className="text-red-100 font-medium">
                Mobile-optimized platform lets you prep on base, at home, or on the go.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-900/90 via-red-900/90 to-blue-900/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="p-8 max-w-5xl mx-auto">
              <h2 className="text-5xl font-black text-white mb-4 uppercase tracking-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
                Your Path to ASVAB Success
              </h2>
              <p className="text-2xl text-white font-bold uppercase tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                Three Simple Steps to Boost Your Military Career
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center bg-white/10 backdrop-blur-md p-8 rounded-2xl border-4 border-white hover:bg-white/20 transition shadow-2xl">
              <div className="w-24 h-24 bg-gradient-to-br from-red-600 to-red-800 text-white rounded-xl flex items-center justify-center text-4xl font-black mx-auto mb-6 shadow-2xl border-4 border-white">
                1
              </div>
              <h3 className="text-3xl font-black text-white mb-4 uppercase drop-shadow-lg">Start Free Trial</h3>
              <p className="text-white font-semibold text-lg drop-shadow-md">
                Get instant access to 5,000+ practice questions, full-length tests, and study tools. No credit card required.
              </p>
            </div>

            <div className="text-center bg-white/10 backdrop-blur-md p-8 rounded-2xl border-4 border-white hover:bg-white/20 transition shadow-2xl">
              <div className="w-24 h-24 bg-white text-gray-900 rounded-xl flex items-center justify-center text-4xl font-black mx-auto mb-6 shadow-2xl border-4 border-gray-800">
                2
              </div>
              <h3 className="text-3xl font-black text-white mb-4 uppercase drop-shadow-lg">Master All Sections</h3>
              <p className="text-white font-semibold text-lg drop-shadow-md">
                Focus on weak areas with targeted practice across all 10 ASVAB subtests to maximize your AFQT score.
              </p>
            </div>

            <div className="text-center bg-white/10 backdrop-blur-md p-8 rounded-2xl border-4 border-white hover:bg-white/20 transition shadow-2xl">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-700 to-blue-900 text-white rounded-xl flex items-center justify-center text-4xl font-black mx-auto mb-6 shadow-2xl border-4 border-white">
                3
              </div>
              <h3 className="text-3xl font-black text-white mb-4 uppercase drop-shadow-lg">Qualify for Your Dream MOS</h3>
              <p className="text-white font-semibold text-lg drop-shadow-md">
                Score high on test day and unlock your choice of military career opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 bg-gradient-to-b from-white/95 to-blue-50/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="p-8 max-w-5xl mx-auto">
              <h2 className="text-5xl font-black text-white uppercase tracking-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
                Trusted by Military Recruits Nationwide
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Marcus J.",
                role: "U.S. Army Infantry",
                content: "Raised my AFQT from 52 to 78 in just 3 weeks. Qualified for the MOS I wanted. Worth every penny!",
                rating: 5
              },
              {
                name: "Sarah T.",
                role: "U.S. Navy Recruit",
                content: "The practice tests felt exactly like the real ASVAB. Scored a 92 AFQT and got my top choice rating!",
                rating: 5
              },
              {
                name: "Alex Chen",
                role: "U.S. Air Force Enlistee",
                content: "Tried other prep sites but StartPatriot's breakdown by section helped me focus on what I needed most.",
                rating: 5
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="p-8 bg-gradient-to-br from-blue-700 to-blue-900 text-white rounded-2xl border-4 border-white shadow-xl hover:scale-105 transition">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-blue-100 mb-6 italic font-medium text-lg">"{testimonial.content}"</p>
                <div className="border-t-2 border-blue-600 pt-4">
                  <p className="font-black text-xl">{testimonial.name}</p>
                  <p className="text-sm text-blue-200 font-semibold uppercase">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-800/95 via-blue-900/95 to-red-800/95 relative overflow-hidden border-t-8 border-white border-b-8">
        {/* Patriotic stripe pattern */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(10)].map((_, i) => (
            <div key={i} className={`absolute h-4 left-0 right-0 ${i % 2 === 0 ? 'bg-white' : 'bg-red-700'}`} style={{ top: `${i * 10}%` }}></div>
          ))}
        </div>

        {/* Star field */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          {[...Array(30)].map((_, i) => (
            <Star key={i} className="absolute w-4 h-4 text-white fill-white" style={{ top: `${(i * 31) % 100}%`, left: `${(i * 67) % 100}%` }} />
          ))}
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="p-8 mb-10">
            <h2 className="text-5xl sm:text-6xl font-black text-white mb-6 uppercase tracking-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
              READY TO ACE THE ASVAB?
            </h2>
            <p className="text-2xl text-white font-bold uppercase tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              Join 12,500+ Patriots Who Boosted Their ASVAB Scores
            </p>
          </div>

          <button
            onClick={handleStartFreeTrial}
            className="px-12 py-6 bg-white text-blue-900 text-2xl font-black rounded-xl hover:bg-blue-50 transition shadow-2xl hover:shadow-3xl inline-flex items-center gap-3 border-4 border-blue-900 uppercase"
          >
            <Star className="w-6 h-6 fill-blue-700 text-blue-700" />
            Start Your Free 7-Day Trial
            <ArrowRight className="w-6 h-6" />
          </button>

          <div className="mt-8 inline-block px-8 py-3">
            <p className="text-lg text-white font-black uppercase tracking-widest drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              No credit card required • Full access • Cancel anytime
            </p>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-3 px-6 py-4">
              <Check className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
              <span className="font-black text-lg uppercase text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">5,000+ Questions</span>
            </div>
            <div className="flex items-center gap-3 px-6 py-4">
              <Check className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
              <span className="font-black text-lg uppercase text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">Full-Length Tests</span>
            </div>
            <div className="flex items-center gap-3 px-6 py-4">
              <Check className="w-6 h-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
              <span className="font-black text-lg uppercase text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">AFQT Tracking</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-950/95 via-red-950/95 to-blue-950/95 text-gray-300 py-16 border-t-8 border-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-white to-red-600 rounded-full flex items-center justify-center shadow-lg">
                  <Star className="w-5 h-5 text-blue-900 fill-blue-900" />
                </div>
                <span className="text-2xl font-black bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent uppercase">StartPatriot</span>
              </div>
              <p className="text-sm font-semibold text-blue-200">
                The most effective way to prepare for the ASVAB exam and serve your country.
              </p>
            </div>

            <div>
              <h4 className="text-white font-black mb-4 uppercase tracking-wide">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition font-semibold">Features</a></li>
                <li><a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="hover:text-white transition font-semibold">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition font-semibold">How It Works</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-black mb-4 uppercase tracking-wide">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition font-semibold">About</a></li>
                <li><a href="#" className="hover:text-white transition font-semibold">Contact</a></li>
                <li><a href="#" className="hover:text-white transition font-semibold">Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-black mb-4 uppercase tracking-wide">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition font-semibold">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition font-semibold">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition font-semibold">FAQ</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t-2 border-blue-800 text-center">
            <p className="text-blue-200 font-bold uppercase tracking-wide">&copy; 2025 StartPatriot. All rights reserved.</p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default LandingPage;
